/**
 * Sketch (`/designs/<id>/sketch`): the installer draws the roof on a satellite basemap and places the panels;
 * every save is a normal design version (quote, proposal, BOM, options and the Designer work on it). The same
 * tools refine an automatic design's detected roof (unchanged detected faces stay as they were).
 *
 * Composition (a designer can restyle every part in sketch.css without touching logic):
 *   SketchTopBar · SketchToolbar (rail) · ToolOptions · SketchCanvas (2D) | Sketch3D (3D) · side panel sections
 *   (ProductionSection, ShadingSection, ShadeObjectSection, RoofSection, PanelsSection, AiDesignCard) · ShapeChooser.
 * Logic: skeleton.ts (lines → faces → slopes), templates.ts (footprint → roof shapes), roofModel.ts (the API's
 * documents), gizmo.ts (moving, turning, magnetic placement), shade.ts (trees, obstacles, neighbours, shadows, the
 * live solar-access estimate), state.ts (undo/redo, what a save sends).
 *
 * Keys: V select · R footprint · L lines · P panels · T trees · C roof obstacles · N buildings and walls · H pan ·
 * A solar-access heatmap · M magnetic/free · O portrait/landscape · 2/3 views · Delete · ⌘/Ctrl Z, ⇧⌘/Ctrl Z ·
 * ⌘/Ctrl S save · Esc · ? the cheat sheet (the wheel gestures, editor/hotkeys.ts scope "sketch", rebindable).
 *
 * The tools: the rail's flyouts pick a tool's variant (variants.ts); the stage bar docked above the map names the
 * active tool and holds Magnetic | Free and Portrait | Landscape; the side panel's first section is the active tool's
 * options (SketchToolPanel.tsx), lit up when the tool changes. The roof check (gaps.ts): "Close gaps" in the Assist
 * bar, the gaps on the map (2D and 3D) and in the side panel, and a check on every save.
 */
import { ArrowLeft, Box, ChevronUp, ImageOff, Loader2, Lock, Map as MapIcon, PenTool, Redo2, Save, Sparkles, SquareDashed, TriangleAlert, Undo2, X } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useReducer, useRef, useState, type CSSProperties, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { checkoutResult } from "../lib/luxSpend";
import { purchaseArrived } from "../lib/permitDraft";
import { LuxGlyph } from "../components/LuxIcon";
import { ACTION_LABEL, resumeOf, sketchPurchasePrompt, sketchReturnPath, spendableFor, type ResumeAction } from "./purchase.ts";
import type { S } from "../api/client";
import { ApiError, errorMessage } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useLuxWallet } from "../api/queries";
import { useLuxSpend } from "../components/LuxSpend";
import { activityHref, ratePrice } from "../lib/luxSpend";
import { Alert, Button, Modal, Spinner, useToast } from "../components/ui";
import { HelpLink } from "../components/HelpLink";
import type { Vec2 } from "../editor/geo.ts";
import { comboParts, eventCombo, formatCombo, isMac, isTypingTarget, loadOverrides, matchAction, resolveBindings, saveOverrides, type Overrides } from "../editor/hotkeys.ts";
import { CheatSheet } from "../editor/CheatSheet";
import { workspaceForAction } from "../editor/workspace.ts";
import { useWorkspaceSwitch, WorkspaceSwitch } from "../components/WorkspaceSwitch";
import { localConflicts, panelGeometry } from "../editor/layout.ts";
import type { ObstacleItem } from "../editor/model.ts";
import { faceAt } from "../editor/roof.ts";
import { liveRoof } from "../editor/roof.ts";
import type { ModuleSize, Orientation } from "../editor/snap.ts";
import { shownVersion } from "../lib/format";
import { BuildProgress } from "../pages/Designer";
import { fillFace, rotatePanels, gizmoFrame, type PlaceMode } from "./gizmo.ts";
import { config } from "../config";
import { newAssistKey, placeKey, sketchErrorText, useAssist, useBasemapPicture, useDesignWithAi, useEditorDocument, useLiveNumbers, useMapsBrowserKey, useRoofHints, useSaveSketch, useSurroundings } from "./hooks.ts";
import { centroid as ringCentre, liveAccess, sceneOf, sunAt, type ShadeItem } from "./shade.ts";
import { AssistBar, HintsPanel, Nudge } from "./AssistBar";
import { acceptable, afterFailure, applyHints, chargedNow, featureLabel, featureView, granted, hintNudgeFace, hintNudgeLabel, hintRequest, newPageSession, nudgeStorageKey, sessionActive, spendNote, surroundingsNudge, surroundingsStep, withMapsKey, type AssistPageSession, type Hints, type NudgeKey } from "./assist.ts";
import type { Surroundings } from "./Sketch3D";
import { DEFAULT_FACE, toRoofModel } from "./roofModel.ts";
import { removeEdge, resolveSlopes, setEdgeType, updateFace, type EdgeType, type FaceSettings, type StructureKind } from "./skeleton.ts";
import { addStructure, footprintSideOf, hiddenFaces, MAIN, presetRoof, removeStructure, setSidePitch, setStructureEave, structuresOf, structureStats, toggleSide, updateStructure } from "./structures.ts";
import { gableAllowed } from "./footprint.ts";
import { dormerShape, type AlignState } from "./SketchCanvas";
import { DormerSection, FootprintSideCard, StructuresSection } from "./SketchStructures";
import { AlignPanel, BasemapMenu, type AlignMode } from "./SketchBasemap";
import { fromControlPoints, initialPlacement, metresPerPx, plausible, type Affine } from "./align.ts";
import { useAuthedImage, useBasemapActions, useDesignBasemap, useSketchNotes } from "./hooks.ts";
import { newNoteBody, notePins, notesOf } from "./notes.ts";
import { NotesSection, type NoteDraft } from "./SketchNotes";
import type { DormerKind } from "./dormers.ts";
import { AiDesignCard, PanelsSection, ProductionSection, RoofSection, ShadeObjectSection, ShadingSection } from "./SketchSidePanel";
import { SketchCanvas, type CanvasImage } from "./SketchCanvas";
import { SketchToolbar, StageBar, TOOL_KEYS, TOOL_LABELS, ToolIcon, type ShadeOptions } from "./SketchToolbar";
import { sheetAfterSwipe, useSketchLayout, type SheetState } from "./layout.ts";
import { GapsSection, ToolSection } from "./SketchToolPanel";
import { closeGap, closeGaps, findGaps, stepAt, type Gap } from "./gaps.ts";
import { DEFAULT_SHADE_OPTIONS, pickVariant, type ToolState } from "./variants.ts";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { structureLabel } from "./structures.ts";
import { compassName, faceName } from "./text.ts";
import { kbd, Msg, useSketchText } from "./useText.ts";
import type { NotePlace } from "./SketchNotes";
import { initialHistory, isSketchDirty, reduceSketch, sketchDraftFrom } from "./state.ts";
import { applyShape, isRectangle, type RoofShape } from "./templates.ts";
import type { Vec3 } from "../editor/geo.ts";
import type { SketchSelection, SketchTool, SketchView, SunTime } from "./types.ts";
import "./sketch.css";

const Sketch3D = lazy(() => import("./Sketch3D"));
/** Key names (never translated). */
const KEYS_SAVE_MAC = "⌘S";
const KEYS_SAVE = "Ctrl S";
/** Tools that draw on the 2D map only (trees, obstacles, panels and notes also work in 3D). */
const DRAW_2D_TOOLS = new Set<SketchTool>(["footprint", "line", "dormer", "neighbour"]);

export function SketchPage() {
  const { designId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const tx = useSketchText();
  const [version, setVersion] = useState<number | null>(null);
  // the check on save outlives the save (the workspace opens again on the new version)
  const [gapNotice, setGapNotice] = useState(false);
  const doc = useEditorDocument(designId, version);
  // the Assist page session (3D surroundings: one use per page), kept here so a save's remount keeps it
  const [assistSession, setAssistSession] = useState<AssistPageSession>(() => newPageSession(newAssistKey()));
  if (doc.isLoading) return <Spinner label={tx.t("sketch.editor.opening")} />;
  if (doc.error || !doc.data)
    return (
      <div className="sk-error-page">
        <Alert tone="danger" title={tx.t("sketch.editor.openFailed")}>{errorMessage(doc.error)}</Alert>
        <Button onClick={() => navigate(`/designs/${designId}`)}>{tx.t("sketch.editor.back")}</Button>
      </div>
    );
  if (!doc.data.module) return <Navigate to={`/designs/${designId}`} replace />;
  return (
    <SketchWorkspace
      key={`${doc.data.design_id}:${doc.data.version_number}`}
      doc={doc.data}
      gapNotice={gapNotice}
      onGapNotice={setGapNotice}
      assistSession={assistSession}
      setAssistSession={setAssistSession}
      onSaved={(n) => {
        setVersion(null);
        toast(tx.t("sketch.editor.savedAs", { version: shownVersion(n, doc.data!.version_offset) }));
      }}
    />
  );
}

function SketchWorkspace({
  doc,
  onSaved,
  assistSession,
  setAssistSession,
  gapNotice: noticeAtStart = false,
  onGapNotice,
}: {
  doc: S["EditorDocument"];
  onSaved: (n: number) => void;
  assistSession: AssistPageSession;
  setAssistSession: Dispatch<SetStateAction<AssistPageSession>>;
  gapNotice?: boolean;
  onGapNotice?: (on: boolean) => void;
}) {
  const designId = doc.design_id;
  const navigate = useNavigate();
  const tx = useSketchText();
  const org = useOrg();
  const orgId = useOrgId();
  const wallet = useLuxWallet(orgId);
  const lux = useLuxSpend();
  const designCost = ratePrice(wallet.data?.rate_card, "design", 100);
  // back from Buy Lux (#118's flow): `?resume=<action>&checkout=paid|canceled`, offered once the Lux has arrived
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [purchase, setPurchase] = useState<{ action: ResumeAction; result: "paid" | "canceled" } | null>(() => {
    const action = resumeOf(params.get("resume"));
    const result = checkoutResult(params.get("checkout"));
    return action && result ? { action, result } : null;
  });
  const [purchaseTimedOut, setPurchaseTimedOut] = useState(false);
  const readOnly = !doc.is_current || !org.can("designs_leads", "edit");
  const [history, dispatch] = useReducer(reduceSketch, doc, initialHistory);
  const draft = history.present;
  const sideCache = useRef<ReactNode>(null);
  // phone and tablet (layout.ts): the side panel is a bottom sheet, the Assist bar a button, the rail collapsible
  const layout = useSketchLayout();
  const compact = layout !== "desktop";
  const [sheet, setSheet] = useState<SheetState>("peek");
  const sheetSwipe = useRef<number | null>(null);
  const [assistOpen, setAssistOpen] = useState(false);
  const [tool, setTool] = useState<SketchTool>(doc.roof_model ? "select" : "footprint");
  const [view, setView] = useState<SketchView>("2d");
  const [lineType, setLineType] = useState<EdgeType>("ridge");
  const [placeMode, setPlaceMode] = useState<PlaceMode>("magnetic");
  const [orientation, setOrientation] = useState<Orientation>((doc.panels[0]?.orientation as Orientation) ?? "portrait");
  const [defaults, setDefaults] = useState<FaceSettings>(DEFAULT_FACE);
  const [selection, setSelection] = useState<SketchSelection>(null);
  const [footprint, setFootprint] = useState<Vec2[] | null>(null);
  const [aiStarted, setAiStarted] = useState<S["NewDesign"] | null>(null);
  const [corners, setCorners] = useState(0);
  // shade: the tools' options, the heatmap and the sun the shadows are drawn for (winter morning: the longest)
  const [shadeOpts, setShadeOpts] = useState<ShadeOptions>(DEFAULT_SHADE_OPTIONS);
  // the cheat sheet (?) and the wheel gestures' bindings (shared with the Designer's, editor/hotkeys.ts)
  const mac = useMemo(() => isMac(), []);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [overrides, setOverridesState] = useState<Overrides>(() => loadOverrides());
  const bindings = useMemo(() => resolveBindings(overrides), [overrides]);
  const setOverrides = (o: Overrides) => {
    setOverridesState(o);
    saveOverrides(o);
  };
  // the roof check: open (the gaps on the map and in the side panel), the one picked, the notice after a save
  const [gapsOpen, setGapsOpen] = useState(false);
  const [gapPicked, setGapPicked] = useState<string | null>(null);
  const [gapNotice, setGapNoticeState] = useState(noticeAtStart);
  const setGapNotice = (on: boolean) => {
    setGapNoticeState(on);
    onGapNotice?.(on);
  };
  const [justClosed, setJustClosed] = useState<number | null>(null);
  const [heatOn, setHeatOn] = useState(false);
  const [sunTime, setSunTime] = useState<SunTime>(() => ({ month: (doc.frame?.origin.lat ?? 45) >= 0 ? 11 : 5, hour: 10 }));
  // Sketch Assist: the bar's helpers, the roof hints under review, the 3D surroundings session of this page
  const automaticDesign = doc.mode !== "sketch";
  const assist = useAssist(designId, !automaticDesign);
  const roofHints = useRoofHints(designId);
  const surroundingsApi = useSurroundings(designId, assistSession.key);
  /** The helper whose click is between its confirmation and its answer: a second click then does nothing. */
  const assistStarting = useRef<string | null>(null);
  const [hints, setHints] = useState<Hints | null>(null);
  const [decided, setDecided] = useState<Record<string, "accepted" | "rejected">>({});
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const surroundingsOn = assistSession.on;
  const setSurroundingsOn = (on: boolean) => setAssistSession((s) => ({ ...s, on }));
  const [assistError, setAssistError] = useState<string | null>(null);
  const [hintsAsked, setHintsAsked] = useState(false);
  const [surNudgeShown, setSurNudgeShown] = useState(false);
  const [dismissed, setDismissed] = useState<Record<NudgeKey, boolean>>(() => ({ roof_hints: readFlag(nudgeStorageKey(designId, "roof_hints")), surroundings: readFlag(nudgeStorageKey(designId, "surroundings")) }));
  // the 3D view and the 3D surroundings button need the browser key: asked once the helper is offered, so its first
  // click from the 2D view does not find it missing
  const surroundingsOffered = !!assist.data?.features.some((f) => f.key === "surroundings" && f.available);
  const maps = useMapsBrowserKey(config.mapsBrowserKey ?? "", !automaticDesign && (view === "3d" || surroundingsOn || surroundingsOffered));
  const mapsKey = maps.key;
  const module = doc.module as ModuleSize & S["ModuleSpec"];
  const lat = doc.frame?.origin.lat ?? 45;
  // phase 4: structures, dormers, eave pick, the basemap choice and uploaded pictures
  const [activeStructure, setActiveStructure] = useState<string>(MAIN);
  const [dormerKind, setDormerKind] = useState<DormerKind>("gable");
  const [picks, setPicks] = useState<Vec3[] | null>(null);
  const basemapData = useDesignBasemap(designId, doc.mode === "sketch");
  const basemapActions = useBasemapActions(designId);
  const [basemapError, setBasemapError] = useState<string | null>(null);
  const [aligning, setAligning] = useState<{ upload: S["BasemapUpload"]; m: Affine; mode: AlignMode; opacity: number; points: { image: Vec2; enu?: Vec2 }[] } | null>(null);
  const alignUrl = useAuthedImage(aligning?.upload.url ?? null);
  // phase 5: the installer's notes (comment threads of kind `note`), pinned in 2D and 3D
  const notesApi = useSketchNotes(designId);
  const notes = useMemo(() => notesOf(notesApi.threads.data?.items), [notesApi.threads.data]);
  const [noteDraft, setNoteDraft] = useState<NoteDraft | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const notesReadOnly = !org.can("designs_leads", "view");

  const model = useMemo(() => toRoofModel(draft.sketch, doc.roof_model), [draft.sketch, doc.roof_model]);
  const roof = useMemo(() => liveRoof(model), [model]);
  const slopes = useMemo(() => resolveSlopes(draft.sketch), [draft.sketch]);
  // the drawn roof's gaps (a Sketch's own drawing; a refined detected roof is not checked)
  const gaps = useMemo(() => (doc.mode === "sketch" ? findGaps(draft.sketch) : []), [draft.sketch, doc.mode]);
  const structures = useMemo(() => structuresOf(draft.sketch), [draft.sketch]);
  const active = structures.find((x) => x.id === activeStructure) ?? structures[0]!;
  const hidden = useMemo(() => hiddenFaces(draft.sketch), [draft.sketch]);
  const drawDefaults: FaceSettings = { ...defaults, structureId: active.id, eaveHeight: active.eaveHeight };
  const faceIds = useMemo(() => new Set(draft.sketch.faces.map((f) => f.id)), [draft.sketch.faces]);
  const panels = useMemo(() => draft.panels.filter((p) => faceIds.has(p.face_id)), [draft.panels, faceIds]);
  const live = useLiveNumbers(designId, doc, draft);
  // the setbacks of the roof as drawn (the last preview's; the version's while the roof is unchanged)
  const setbacks = draft.dirty.roof ? setbacksFor(live.setbacks, model) : doc.setbacks ?? null;
  const conflicts = useMemo(() => localConflicts(panels, roof, draft.obstacles, setbacks, module), [panels, roof, draft.obstacles, setbacks, module]);
  // nothing differed from the saved version after all: start again from it (no new version)
  const save = useSaveSketch(designId, doc, onSaved, () => dispatch({ type: "reset", draft: sketchDraftFrom(doc) }));
  const saveToast = useToast(); // a failed save says so where the user looks, not only in the side panel
  const ai = useDesignWithAi(designId);
  const picture = useBasemapPicture(doc.basemap, doc.basemap?.url ?? doc.image?.url);
  const [pictureDismissed, setPictureDismissed] = useState(false);
  const basemap = doc.basemap ?? doc.image;
  const image: CanvasImage | null = basemap ? { url: picture.url, width: basemap.width, height: basemap.height, enuToPx: basemap.enu_to_px } : null;
  const attribution = picture.attribution;
  const dirty = isSketchDirty(draft);
  const weather = live.irradiance?.weather ?? doc.weather;

  const onSketch = useCallback((s: typeof draft.sketch, transient?: boolean) => dispatch({ type: "sketch", sketch: s, transient }), []);
  const onPanels = useCallback((p: typeof draft.panels, transient?: boolean) => dispatch({ type: "panels", panels: p, transient }), []);
  const onObstacles = useCallback((o: ObstacleItem[], transient?: boolean) => dispatch({ type: "obstacles", obstacles: o, transient }), []);
  const onShade = useCallback((t: ShadeItem[], transient?: boolean) => dispatch({ type: "shade", shade: t, transient }), []);
  const ctx = { roof, panels, obstacles: draft.obstacles, setbacks, module, gap: doc.gap_m };
  // the views leave hidden structures out (they are still designed and quoted)
  const shownRoof = useMemo(() => (hidden.size ? { ...roof, faces: roof.faces.filter((f) => !hidden.has(f.id) && !hidden.has(dormerHost(draft.sketch, f.id) ?? "")) } : roof), [roof, hidden, draft.sketch]);
  const structureRows = useMemo(() => {
    const stats = structureStats(draft.sketch);
    const faceOf = new Map(draft.sketch.faces.map((f) => [f.id, f.structureId || MAIN]));
    const kw = (id: string) => (panels.filter((p) => faceOf.get(p.face_id) === id || faceOf.get(dormerHost(draft.sketch, p.face_id) ?? "") === id).length * module.watts) / 1000;
    return structures.map((structure) => ({ structure, faces: stats[structure.id]?.faces ?? 0, area: stats[structure.id]?.area ?? 0, kw: kw(structure.id) }));
  }, [structures, draft.sketch, panels, module.watts]);
  const selectedDormer = selection?.kind === "dormer" ? (draft.sketch.dormers ?? []).find((d) => d.id === selection.id) ?? null : null;
  const selectedSide = (() => {
    if (selection?.kind !== "edge") return null;
    const e = draft.sketch.edges.find((x) => x.id === selection.id);
    const a = e && draft.sketch.vertices[e.a];
    const b = e && draft.sketch.vertices[e.b];
    if (!a || !b) return null;
    const hit = footprintSideOf(draft.sketch, a, b);
    if (!hit?.structure.footprint) return null;
    const spec = hit.structure.footprint.edges[hit.side]!;
    return { ...hit, mode: spec.mode, pitch: spec.pitch, allowed: gableAllowed(hit.structure.footprint.ring, hit.side) };
  })();
  const flipSide = () => {
    if (!selectedSide || readOnly) return;
    const next = toggleSide(draft.sketch, selectedSide.structure.id, selectedSide.side, drawDefaults);
    if (next !== draft.sketch) {
      onSketch(next);
      // the line the installer clicked is gone: select the side again where it now is
      const fp = selectedSide.structure.footprint!;
      const a = fp.ring[selectedSide.side]!, b = fp.ring[(selectedSide.side + 1) % fp.ring.length]!;
      const again = next.edges.find((x) => {
        const p = next.vertices[x.a], q = next.vertices[x.b];
        return p && q && footprintSideOf(next, p, q)?.side === selectedSide.side && Math.hypot((p[0] + q[0]) / 2 - (a[0] + b[0]) / 2, (p[1] + q[1]) / 2 - (a[1] + b[1]) / 2) < Math.hypot(b[0] - a[0], b[1] - a[1]) / 2 + 1e-6;
      });
      setSelection(again ? { kind: "edge", id: again.id } : null);
    }
  };
  // eave pick: the ground, then the eave (on the 3D surroundings or the model): the height between them
  const onPick = (q: Vec3) => {
    const got = [...(picks ?? []), q];
    if (got.length < 2) return setPicks(got);
    const h = Math.abs(got[1]![2] - got[0]![2]);
    setPicks(null);
    if (h > 0.3) onSketch(setStructureEave(draft.sketch, active.id, h));
  };
  // placing an uploaded picture
  const startAlign = (upload: S["BasemapUpload"]) => {
    const centre: Vec2 = [0, 0];
    const m = (upload.px_to_enu as Affine | null) ?? initialPlacement(upload.width, upload.height, centre, Math.max(0.01, 60 / Math.max(upload.width, upload.height)));
    setAligning({ upload, m, mode: upload.px_to_enu ? "drag" : "points", opacity: 0.75, points: [] });
    setView("2d");
  };
  const alignFit = aligning && aligning.mode === "points" && aligning.points.filter((q) => q.enu).length >= 2 ? fromControlPoints(aligning.points.filter((q) => q.enu).map((q) => ({ image: q.image, enu: q.enu! }))) : null;
  const alignState: AlignState | null = aligning
    ? {
        url: alignUrl,
        width: aligning.upload.width,
        height: aligning.upload.height,
        m: alignFit?.m ?? aligning.m,
        mode: aligning.mode,
        opacity: aligning.opacity,
        points: aligning.points,
        onChange: (m) => setAligning((a) => (a ? { ...a, m } : a)),
        onPoint: (q) =>
          setAligning((a) => {
            if (!a) return a;
            if (q.image) return { ...a, points: [...a.points, { image: q.image }] };
            const points = a.points.map((x, i) => (i === a.points.length - 1 ? { ...x, enu: q.enu } : x));
            const done = points.filter((x) => x.enu);
            const fit = done.length >= 2 ? fromControlPoints(done.map((x) => ({ image: x.image, enu: x.enu! }))) : null;
            return { ...a, points, m: fit?.m ?? a.m };
          }),
      }
    : null;
  const saveAlign = () => {
    if (!aligning) return;
    const done = aligning.points.filter((q) => q.enu);
    const body: S["BasemapAlignment"] = aligning.mode === "points" && done.length >= 2 ? { control_points: done.map((q) => ({ image_px: q.image, enu: q.enu! })) } : { px_to_enu: (alignFit?.m ?? aligning.m) as number[] };
    basemapActions.align.mutate(
      { id: aligning.upload.id, body },
      {
        onSuccess: (u) => {
          setAligning(null);
          basemapActions.choose.mutate({ provider: "upload", upload_id: u.id });
        },
        onError: (e) => setBasemapError(errorMessage(e)),
      },
    );
  };
  const shading = live.irradiance?.shading ?? (draft.dirty.roof || draft.dirty.obstacles || draft.dirty.shade ? null : doc.shading ?? null);
  const drawnCount = draft.shade.length + draft.obstacles.length;
  const sun = useMemo(() => (drawnCount ? sunAt(lat, sunTime.month, sunTime.hour) : null), [drawnCount, lat, sunTime.month, sunTime.hour]);
  // the heatmap: the server's shading for the modules as drawn now, else a quick estimate in the browser
  const heatCache = useRef<Map<string, number> | null>(null);
  const heat = useMemo(() => {
    if (!heatOn) return null;
    // mid-drag the modules keep the colours they had (the browser's estimate is redone once, on the drop)
    if (history.pending != null && heatCache.current) return heatCache.current;
    const out = new Map<string, number>();
    const fresh = live.for === draft ? live.access : !isSketchDirty(draft) ? new Map(doc.panels.filter((p) => p.solar_access != null).map((p) => [placeKey(p.face_id, p.center_uv), p.solar_access!])) : null;
    const missing = panels.filter((p) => {
      const v = fresh?.get(placeKey(p.face_id, [Math.round(p.center[0] * 1e4) / 1e4, Math.round(p.center[1] * 1e4) / 1e4]));
      if (v != null) out.set(p.key, v);
      return v == null;
    });
    if (missing.length) {
      const scene = sceneOf(draft.shade, draft.obstacles.map((o) => {
        const f = faceAt(roof, ringCentre(o.polygon));
        const c = ringCentre(o.polygon);
        return { polygon: o.polygon, height_m: o.height_m, base: f ? -(f.normal[0] * c[0] + f.normal[1] * c[1] + f.d) / f.normal[2] : 0 };
      }));
      const points = missing.map((p) => {
        const g = panelGeometry(p, roof, module);
        const c = g.xyz.length ? (g.xyz.reduce((a, q) => [a[0] + q[0] / 4, a[1] + q[1] / 4, a[2] + q[2] / 4], [0, 0, 0.12]) as [number, number, number]) : ([0, 0, 0] as [number, number, number]);
        return { xyz: c, normal: (g.face?.normal ?? [0, 0, 1]) as [number, number, number] };
      });
      liveAccess(points, scene, lat).forEach((v, i) => out.set(missing[i]!.key, v));
    }
    heatCache.current = out;
    return out;
  }, [heatOn, live.for, live.access, draft, panels, roof, module, lat, doc.panels, history.pending]);
  const faceAccess = useMemo(() => {
    const byId = new Map((shading?.faces ?? []).map((f) => [f.face_id, f.solar_access]));
    return draft.sketch.faces.map((f, index) => ({ id: f.id, index, label: faceName(tx, f.label, index), access: byId.get(f.id) ?? null }));
  }, [shading, draft.sketch.faces, tx]);
  const selShade = selection?.kind === "shade" ? draft.shade.find((t) => t.key === selection.key) ?? null : null;
  const selObstacle = selection?.kind === "obstacle" ? draft.obstacles.find((o) => o.key === selection.key) ?? null : null;
  const selectedFace = selection?.kind === "face" ? roof.faces.find((f) => f.id === selection.id) ?? null : null;
  const selectedKeys = selection?.kind === "panels" ? new Set(selection.keys) : new Set<string>();

  // ------------------------------------------------------------------------------------ Sketch Assist
  const features = useMemo(() => withMapsKey(tx, assist.data?.features ?? null, maps.missing), [tx, assist.data, maps.missing]);
  // what each action may spend: Design with AI may use the Lux reserved for designs, the Assist helpers may not
  const assistBalance = spendableFor("roof_hints", wallet.data) ?? assist.data?.balance ?? null;
  const aiBalance = spendableFor("ai", wallet.data);
  /** Buy Lux from a confirmation: the Sketch is saved first and checkout comes back here offering `action`. */
  const leaving = (action: ResumeAction) => ({
    returnTo: sketchReturnPath(designId, action),
    beforeLeave: async () => {
      if (isSketchDirty(history.present) && !readOnly) await save.mutateAsync({ draft: history.present, note: tx.t("sketch.editor.savedBeforeLux") });
    },
  });
  const dismissNudge = (key: NudgeKey) => {
    writeFlag(nudgeStorageKey(designId, key));
    setDismissed((d) => ({ ...d, [key]: true }));
  };
  const refundToast = (e: unknown, action: "roof_hints" | "surroundings", reason: MessageKey) => {
    const refunded = e instanceof ApiError ? Number((e.problem as { refunded?: number }).refunded ?? 0) : 0;
    if (refunded > 0) lux.refunded({ amount: refunded, label: tx.t(ACTION_LABEL[action]), reason: tx.t(reason), href: activityHref(action === "roof_hints" ? "sketch.roof_hints" : "sketch.surroundings") });
  };
  const runAssist = async (key: string, anchor: HTMLElement | null) => {
    const f = features?.find((x) => x.key === key);
    if (!f || readOnly) return;
    setAssistError(null);
    if (key === "surroundings") {
      // one use per page session: hiding, showing again and switching 2D/3D never charge again
      const step = surroundingsStep(assistSession, surroundingsApi.start.isPending || assistStarting.current === key);
      if (step === "hide") return setSurroundingsOn(false);
      setView("3d");
      if (step === "show") return setSurroundingsOn(true);
      if (step === "wait") return;
      if (!mapsKey) return setAssistError(tx.t("sketch.editor.surroundingsUnavailable"));
    }
    if (assistStarting.current === key || (key === "roof_hints" && roofHints.isPending)) return; // a second click
    if (key === "roof_hints" && !draft.sketch.faces.length) return setAssistError(tx.t("sketch.editor.drawRoofFirst"));
    const v = featureView(tx, f, assistBalance, { sessionActive: sessionActive(assistSession) });
    // nothing to pay (included, or already bought for this design): no confirmation
    const req = { cost: v.cost, label: featureLabel(tx, f), balance: assistBalance, worthMinor: f.price_minor, canPay: v.cost > 0 ? f.can_pay : true, note: spendNote(tx, key), ...leaving(key as ResumeAction) };
    assistStarting.current = key;
    const done = () => {
      if (assistStarting.current === key) assistStarting.current = null;
    };
    if (!(await lux.confirm(req, anchor))) return done();
    // a 402 (the wallet cannot pay after all): the out-of-Lux popover, with Buy Lux coming back here
    const outOfLux = (e: unknown) => e instanceof ApiError && e.status === 402 && (void lux.confirm({ ...req, canPay: false }, anchor), true);
    if (key === "roof_hints") {
      setHintsAsked(true);
      roofHints.mutate(hintRequest(draft.sketch), {
        onSuccess: (r) => {
          setHints(r);
          setDecided({});
          const charged = chargedNow(r.use);
          if (charged > 0) lux.spent({ amount: charged, label: tx.t("sketch.action.roofHints"), left: r.use.lux_left ?? null, href: activityHref("sketch.roof_hints") });
        },
        onError: (e) => {
          if (outOfLux(e)) return;
          setAssistError(errorMessage(e));
          refundToast(e, "roof_hints", "sketch.editor.refund.noRoofData");
        },
        onSettled: done,
      });
      return;
    }
    surroundingsApi.start.mutate(undefined, {
      onSuccess: (r) => {
        setAssistSession((s) => granted(s, r));
        const charged = chargedNow(r.use);
        if (charged > 0) lux.spent({ amount: charged, label: tx.t("sketch.action.surroundings"), left: r.use.lux_left ?? null, href: activityHref("sketch.surroundings") });
      },
      onSettled: done,
      onError: (e) => {
        if (outOfLux(e)) return;
        setAssistError(errorMessage(e));
        refundToast(e, "surroundings", "sketch.editor.refund.couldNotStart");
      },
    });
  };
  const onSurroundingsError = (_cause: string) => {
    // the tiles' own reason is technical (logged in the console by editor/tiles3d.ts): the installer reads this
    const message = tx.t("sketch.editor.surroundingsFailed");
    const grant = assistSession.grant;
    if (!grant || grant.reported) {
      setSurroundingsOn(false);
      return setAssistError(message);
    }
    // refunded once; the next try is a new session (a new key), charged as usual
    setAssistSession((s) => afterFailure(s, newAssistKey()));
    surroundingsApi.failed.mutate(
      // the report's reason is for the logs (English, like the server's causes), never shown
      { useId: grant.useId, reason: "The 3D surroundings didn't load." },
      {
        onSuccess: (use) => {
          setAssistError(tx.t(use.lux > 0 ? "sketch.editor.surroundingsRefunded" : "sketch.editor.surroundingsNotCounted"));
          if (use.lux > 0 && use.refunded) lux.refunded({ amount: use.lux, label: tx.t("sketch.action.surroundings"), reason: tx.t("sketch.editor.refund.didNotLoad"), left: use.lux_left ?? null, href: activityHref("sketch.surroundings") });
        },
        onError: () => setAssistError(message),
      },
    );
  };
  const acceptHints = (ids: string[]) => {
    if (!hints || !ids.length) return;
    onSketch(applyHints(draft.sketch, hints.faces, ids));
    setDecided((d) => ({ ...d, ...Object.fromEntries(ids.map((id) => [id, "accepted" as const])) }));
    setFlash(new Set(ids));
    window.setTimeout(() => setFlash(new Set()), 1400);
  };
  const openHints = hints ? acceptable(hints.faces, draft.sketch).filter((h) => !decided[h.face_id]).map((h) => h.face_id) : [];
  const hintBoxes = hints
    ? hints.segments.map((seg) => ({ points: seg.box as Vec2[], matched: seg.faces.length > 0, label: tx.t("sketch.editor.hintBox", { deg: tx.num(seg.pitch_deg, 0), dir: compassName(tx, seg.azimuth_deg) }) }))
    : undefined;
  const roofHintsFeature = features?.find((f) => f.key === "roof_hints");
  const surFeature = features?.find((f) => f.key === "surroundings");
  const nudgeFace =
    view === "2d" && tool === "select" && !footprint && !hints && !roofHints.isPending
      ? hintNudgeFace(draft.sketch, { asked: hintsAsked, dismissed: dismissed.roof_hints, available: !!roofHintsFeature?.available && !readOnly })
      : null;
  const showSurNudge = surroundingsNudge({ view, on: surroundingsOn, granted: sessionActive(assistSession), shown: surNudgeShown, dismissed: dismissed.surroundings, available: !!surFeature?.available && !!mapsKey && !readOnly });
  const grant = assistSession.grant;
  const shownSurroundings = useMemo<Surroundings | null>(
    () => (surroundingsOn && grant && !grant.reported && mapsKey ? { apiKey: mapsKey, origin: grant.origin, groundElevationM: grant.groundElevationM } : null),
    [surroundingsOn, grant, mapsKey],
  );

  const toolState: Omit<ToolState, "tool"> = { shade: shadeOpts, placeMode, orientation, dormer: dormerKind, lineType };
  const onVariant = (t: SketchTool, id: string) => {
    const next = pickVariant({ tool, ...toolState }, t, id);
    setShadeOpts(next.shade);
    setPlaceMode(next.placeMode);
    setOrientation(next.orientation);
    setDormerKind(next.dormer);
    setLineType(next.lineType);
    setTool(next.tool);
  };
  const wheelKeys = (id: string) => comboParts(bindings.get(id)?.[0] ?? "", mac).slice(0, -1).join(" ") || "—";
  // the roof check
  const openGaps = () => {
    setGapsOpen(true);
    setGapNotice(false);
    setJustClosed(null);
    setGapPicked(null);
  };
  const fixGap = (g: Gap) => {
    if (readOnly) return;
    onSketch(closeGap(draft.sketch, g, drawDefaults));
    setGapPicked(null);
    setJustClosed(1);
  };
  const fixAllGaps = () => {
    if (readOnly) return;
    const r = closeGaps(draft.sketch, drawDefaults);
    if (r.closed) onSketch(r.sketch);
    setGapPicked(null);
    setGapNotice(false);
    setJustClosed(r.closed);
  };
  const gapMarks = gapsOpen ? { list: gaps, selected: gapPicked, onSelect: setGapPicked } : null;
  const gaps3d = useMemo(
    () => (gapsOpen ? gaps.map((g, i) => {
      const step = stepAt(draft.sketch, g);
      const z = step ? step : (() => {
        const f = faceAt(roof, g.at);
        const h = f ? -(f.normal[0] * g.at[0] + f.normal[1] * g.at[1] + f.d) / f.normal[2] : 0;
        return [h, h] as [number, number];
      })();
      return { id: g.id, n: i + 1, at: g.at, lo: z[0], hi: z[1], selected: gapPicked === g.id };
    }) : undefined),
    [gapsOpen, gaps, gapPicked, draft.sketch, roof],
  );

  const deleteSelection = () => {
    if (readOnly || !selection) return;
    if (selection.kind === "panels") onPanels(draft.panels.filter((p) => !selectedKeys.has(p.key)));
    if (selection.kind === "shade") onShade(draft.shade.filter((t) => t.key !== selection.key));
    if (selection.kind === "obstacle") onObstacles(draft.obstacles.filter((o) => o.key !== selection.key));
    if (selection.kind === "edge") onSketch(removeEdge(draft.sketch, selection.id, defaults));
    if (selection.kind === "dormer") onSketch({ ...draft.sketch, dormers: (draft.sketch.dormers ?? []).filter((d) => d.id !== selection.id) });
    if (selection.kind === "vertex") {
      let s = draft.sketch;
      for (const e of s.edges.filter((x) => x.a === selection.id || x.b === selection.id)) s = removeEdge(s, e.id, defaults);
      onSketch(s);
    }
    setSelection(null);
  };
  const selectAllPanels = () => {
    if (!panels.length) return;
    setSelection({ kind: "panels", keys: panels.map((x) => x.key) });
    setTool("select"); // the ring gizmo (move, turn) is the select tool's
  };
  const rotateSelection = (deg: number) => {
    const g = gizmoFrame(panels, selectedKeys, roof, module);
    if (g) onPanels(rotatePanels(draft.panels, selectedKeys, g.face, g.centreUv, deg));
  };
  const chooseShape = (shape: RoofShape) => {
    if (!footprint) return;
    // hip and gable come from the straight skeleton (any outline); shed and flat are one face
    const next =
      shape === "hip" || shape === "gable" || shape === "gable-across"
        ? presetRoof(draft.sketch, active.id, footprint, shape, drawDefaults)
        : updateStructure(applyShape(draft.sketch, footprint, shape, drawDefaults, lat), active.id, { footprint: null });
    onSketch(next);
    setFootprint(null);
    setTool("select");
  };
  const startAi = async (anchor: HTMLElement | null) => {
    const req = { cost: designCost, label: tx.t("sketch.action.ai"), balance: aiBalance, worthMinor: wallet.data?.unit_minor != null ? Math.floor((designCost * wallet.data.unit_minor) / 100) : null, ...leaving("ai") };
    if (!(await lux.confirm(req, anchor))) return;
    ai.mutate(undefined, {
      onSuccess: (made) => {
        setPurchase(null);
        lux.spent({ amount: designCost, label: tx.t("sketch.action.ai"), left: made.lux_left ?? null, href: activityHref("design") });
        setAiStarted(made);
      },
      onError: (e) => {
        if (e instanceof ApiError && e.status === 402) void lux.confirm({ ...req, canPay: false }, anchor);
      },
    });
  };
  // back from checkout: clear the URL, say so when cancelled, wait for the Lux, then offer the action again
  const purchaseCost = purchase ? (purchase.action === "ai" ? designCost : featureView(tx, features?.find((f) => f.key === purchase.action) ?? ({ price: 0 } as never), assistBalance).cost) : 0;
  const purchaseSpendable = purchase ? spendableFor(purchase.action, wallet.data) : null;
  const purchaseIn = !!purchase && purchase.result === "paid" && (purchaseTimedOut || purchaseArrived(null, purchaseSpendable, purchaseCost));
  useEffect(() => {
    if (!purchase) return;
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.delete("checkout");
      next.delete("resume");
      return next;
    }, { replace: true });
    if (purchase.result === "canceled") {
      setAssistError(tx.t("sketch.editor.checkoutCancelled"));
      setPurchase(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (purchase?.result !== "paid" || purchaseIn) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > 30_000) setPurchaseTimedOut(true);
      else void qc.invalidateQueries({ queryKey: ["lux", orgId] });
    }, 2000);
    return () => window.clearInterval(id);
  }, [purchase, purchaseIn, qc, orgId]);
  const purchasePrompt = purchase?.result === "paid" ? sketchPurchasePrompt(tx, purchase.action, purchaseSpendable, purchaseCost, purchaseIn) : null;
  const runPurchased = (anchor: HTMLElement) => {
    if (!purchase) return;
    const action = purchase.action;
    setPurchase(null);
    if (action === "ai") void startAi(anchor);
    else void runAssist(action, anchor);
  };

  /** One save at a time: a double click on Save sent a second save of the same version (the state's isPending is not
   * set yet within the same tick), which the API refused as a conflict, unseen (QA round 1, EX-03). */
  const saving = useRef(false);
  const doSave = () => {
    if (!dirty || readOnly || save.isPending || saving.current) return;
    saving.current = true;
    // a light check on every save: the roof saves as drawn, and the gaps are offered to close
    if (gaps.length && !gapsOpen) setGapNotice(true);
    save.mutate({ draft, note: "" }, { onError: (e) => saveToast(sketchErrorText(tx, e), true), onSettled: () => void (saving.current = false) });
  };

  // Design | Sketch | Permit (editor/workspace.ts): the Designer's keys (rebindable there), save first or ask
  const workspaceDesign = { mode: doc.mode, hasRoof: !!doc.roof_model || roof.faces.length > 0 };
  const workspace = useWorkspaceSwitch({
    designId, current: "sketch", state: { dirty, readOnly }, design: workspaceDesign,
    save: () => save.mutateAsync({ draft: history.present, note: "" }),
  });
  // the same bindings as the cheat sheet's (the Designer's keys, rebindable, and the wheel gestures)
  const keyBindings = bindings;
  const keyLabel = (id: string) => (keyBindings.get(id)?.[0] ? formatCombo(keyBindings.get(id)![0]!, mac) : null);
  const workspaceKeys = { design: keyLabel("mode.design"), sketch: keyLabel("mode.sketch"), permit: keyLabel("mode.permit") };

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target as HTMLElement | null)) return;
      const switchTo = workspaceForAction(matchAction(e, keyBindings, { scope: "2d", mac, typing: false, repeat: e.repeat })?.id ?? "", "sketch");
      if (switchTo) {
        e.preventDefault();
        return workspace.go(switchTo);
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? "redo" : "undo" });
        return;
      }
      if (mod && !e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        return selectAllPanels();
      }
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        doSave();
        return;
      }
      if (bindings.get("help.sheet")?.includes(eventCombo(e, mac))) {
        e.preventDefault();
        return setSheetOpen((o) => !o);
      }
      if (mod || e.altKey) return;
      const tools = Object.entries(TOOL_KEYS).find(([, k]) => k.toLowerCase() === e.key.toLowerCase());
      if (tools) return setTool(tools[0] as SketchTool);
      if (e.key === "Delete" || e.key === "Backspace") return deleteSelection();
      if (e.key === "Escape") {
        if (sheetOpen) return setSheetOpen(false);
        if (gapPicked) return setGapPicked(null);
        if (noteDraft) return setNoteDraft(null);
        if (picks) return setPicks(null);
        if (aligning) return setAligning(null);
        return setSelection(null);
      }
      if (e.key.toLowerCase() === "g" && selectedSide) return flipSide();
      if (e.key.toLowerCase() === "m") return setPlaceMode((m) => (m === "magnetic" ? "free" : "magnetic"));
      if (e.key.toLowerCase() === "o") return setOrientation((o) => (o === "portrait" ? "landscape" : "portrait"));
      if (e.key.toLowerCase() === "a") return setHeatOn((on) => !on);
      if (e.key === "2") return setView("2d");
      if (e.key === "3") return setView("3d");
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  // the drawing tools work on the 2D map: picking one in 3D goes to 2D (in 3D a click did nothing)
  useEffect(() => {
    if (view === "3d" && DRAW_2D_TOOLS.has(tool)) setView("2d");
  }, [tool]); // eslint-disable-line react-hooks/exhaustive-deps
  const wasIn3d = useRef(false);
  useEffect(() => {
    if (view === "3d") wasIn3d.current = true;
    else if (wasIn3d.current) setSurNudgeShown(true); // offered once per session: leaving 3D ends the offer
  }, [view]);
  // fetch the 3D view's code while the installer draws, so the switch to 3D is instant
  const hasRoof = roof.faces.length > 0;
  useEffect(() => {
    if (!hasRoof) return;
    const idle = window.requestIdleCallback ?? ((f: () => void) => window.setTimeout(f, 800));
    idle(() => void import("./Sketch3D"));
  }, [hasRoof]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const selectedNote = selection?.kind === "note" ? selection.id : null;
  const pins = useMemo(() => {
    const out = notePins(notes, selectedNote);
    return noteDraft ? [...out, { id: "draft", n: notes.length + 1, at: noteDraft.at, selected: true }] : out;
  }, [notes, selectedNote, noteDraft]);
  const faceLabel = (faceId: string | null): NotePlace => {
    if (!faceId) return null;
    const i = draft.sketch.faces.findIndex((f) => f.id === faceId);
    if (i >= 0) return { kind: "face", name: faceName(tx, draft.sketch.faces[i]!.label, i) };
    return roof.faces.some((f) => f.id === faceId) ? { kind: "dormer" } : null;
  };
  const noteHandlers = {
    notes: pins,
    onNote: (id: string) => id !== "draft" && setSelection({ kind: "note", id }),
    onNoteAt: notesReadOnly ? undefined : (at: Vec2, faceId: string | null) => {
      setNoteError(null);
      setNoteDraft({ at: [at[0], at[1]], faceId });
      setSelection(null);
    },
  };
  const createNote = (text: string, print: { proposal: boolean; planSet: boolean }) => {
    if (!noteDraft) return;
    notesApi.create.mutate(newNoteBody(noteDraft.at, noteDraft.faceId, doc.version_number, text, print), {
      onSuccess: (t) => {
        setNoteDraft(null);
        setSelection({ kind: "note", id: t.id });
        setTool("select");
      },
      onError: (e) => setNoteError(errorMessage(e)),
    });
  };
  const changeNote = (id: string, body: S["DesignCommentThreadUpdate"]) => {
    setNoteError(null);
    notesApi.update.mutate({ id, body }, { onError: (e) => setNoteError(errorMessage(e)), onSuccess: () => body.status === "resolved" && setSelection(null) });
  };
  const shared = {
    image, attribution, roof: shownRoof, setbacks, panels, conflicts, module, gap: doc.gap_m, tool, placeMode, orientation, selection, readOnly, onSelect: setSelection, onPanels, onCheckpoint: () => dispatch({ type: "checkpoint" }),
    obstacles: draft.obstacles, shade: draft.shade, shadeOptions: shadeOpts, onObstacles, onShade, heat, sun,
    ...noteHandlers,
    // the wheel gestures, the same over the 2D map and the 3D model
    bindings, mac, onShadeOptions: (o: Partial<ShadeOptions>) => setShadeOpts((x) => ({ ...x, ...o })), onOrientation: setOrientation, onTurnPanels: rotateSelection,
  };
  const stageHint = toolHint(tool, shadeOpts.neighbour, wheelKeys("sketch.wheelSize"), wheelKeys("sketch.wheelKind"));
  const automatic = doc.mode !== "sketch";
  const panelsSection = (
    <PanelsSection
      module={module}
      count={panels.length}
      selected={selectedKeys.size}
      orientation={orientation}
      placeMode={placeMode}
      faceSelected={!!selectedFace}
      readOnly={readOnly}
      onOrientation={setOrientation}
      onPlaceMode={setPlaceMode}
      onFill={() => selectedFace && onPanels([...draft.panels, ...fillFace(ctx, selectedFace, orientation)])}
      onClearFace={() => selectedFace && onPanels(draft.panels.filter((p) => p.face_id !== selectedFace.id))}
      onRotate={rotateSelection}
      onDelete={deleteSelection}
      onDeselect={() => setSelection(null)}
      onSelectAll={selectAllPanels}
      highlight={tool === "panel"}
    />
  );
  // the side panel shows nothing a drag changes until it ends (counts, live numbers): while one is under way (a
  // transient edit waiting for its checkpoint) the same element is returned, so React skips the whole panel on every
  // pointer move of a 500-module drag
  const dragging = history.pending != null;
  const sidePanel = dragging && sideCache.current ? sideCache.current : (sideCache.current = (
        <aside className={`sk-side${compact ? ` is-sheet is-${sheet}` : ""}`} aria-label={tx.t("sketch.editor.details")}>
          {compact && (
            <button
              type="button"
              className="sk-sheet-handle"
              aria-expanded={sheet === "open"}
              aria-label={tx.t(sheet === "open" ? "sketch.editor.hideDetails" : "sketch.editor.showDetails")}
              onPointerDown={(e) => (sheetSwipe.current = e.clientY)}
              onPointerUp={(e) => {
                const from = sheetSwipe.current;
                sheetSwipe.current = null;
                if (from != null) setSheet((st) => sheetAfterSwipe(st, e.clientY - from));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSheet((st) => (st === "open" ? "peek" : "open"));
                }
              }}
            >
              <i className="sk-sheet-grip" aria-hidden />
              <span className="sk-sheet-tool"><ToolIcon tool={tool} size={15} /> {tx.t(TOOL_LABELS[tool])}</span>
              <span className="sk-sheet-size"><b>{tx.num(panels.length ? live.numbers.kw_dc ?? 0 : 0, 2)}</b> kW · {tx.t("sketch.editor.panelsCount", { count: panels.length })}</span>
              <ChevronUp size={16} className="sk-sheet-chevron" aria-hidden />
            </button>
          )}
          <ToolSection
            key={tool}
            tool={tool}
            state={toolState}
            readOnly={readOnly}
            wheel={{ size: wheelKeys("sketch.wheelSize"), kind: wheelKeys("sketch.wheelKind"), height: wheelKeys("sketch.wheelHeight") }}
            structureName={active.name}
            notes={notes.length}
            onVariant={onVariant}
            onShade={(o) => setShadeOpts((x) => ({ ...x, ...o }))}
            panels={tool === "panel" ? panelsSection : undefined}
          />
          {gapsOpen && (
            <GapsSection gaps={gaps} selected={gapPicked} readOnly={readOnly} justClosed={justClosed} onSelect={setGapPicked} onFix={fixGap} onFixAll={fixAllGaps} onClose={() => setGapsOpen(false)} />
          )}
          <ProductionSection numbers={live.numbers} weather={weather} pricing={live.pricing} panels={panels.length} shading={shading} />
          {live.error && <p className="sk-error">{live.error}</p>}
          {save.error && <p className="sk-error">{sketchErrorText(tx, save.error)}</p>}
          {selectedDormer && (
            <DormerSection
              dormer={selectedDormer}
              valid={dormerShape(roof, selectedDormer).valid}
              readOnly={readOnly}
              onChange={(d) => onSketch({ ...draft.sketch, dormers: (draft.sketch.dormers ?? []).map((x) => (x.id === d.id ? d : x)) })}
              onDelete={deleteSelection}
              onDeselect={() => setSelection(null)}
            />
          )}
          {selectedSide && (
            <FootprintSideCard
              structure={selectedSide.structure.name}
              mode={selectedSide.mode}
              pitch={selectedSide.pitch}
              allowed={selectedSide.allowed}
              readOnly={readOnly}
              onToggle={flipSide}
              onPitch={(deg) => onSketch(setSidePitch(draft.sketch, selectedSide.structure.id, selectedSide.side, deg, drawDefaults))}
            />
          )}
          <StructuresSection
            rows={structureRows}
            active={active.id}
            readOnly={readOnly}
            picking={!!picks}
            onActive={setActiveStructure}
            onRename={(id, name) => onSketch(updateStructure(draft.sketch, id, { name }))}
            onHidden={(id, h) => onSketch(updateStructure(draft.sketch, id, { hidden: h }))}
            onDelete={(id) => {
              onSketch(removeStructure(draft.sketch, id));
              if (id === activeStructure) setActiveStructure(MAIN);
            }}
            onAdd={(kind: StructureKind) => {
              const made = addStructure(draft.sketch, kind);
              onSketch(made.sketch);
              setActiveStructure(made.id);
              setTool("footprint");
              setView("2d");
            }}
            onEave={(id, m) => onSketch(setStructureEave(draft.sketch, id, m))}
            onPick={() => {
              if (picks) return setPicks(null);
              setPicks([]);
              setView("3d");
            }}
          />
          <NotesSection
            notes={notes}
            selected={selectedNote}
            draft={noteDraft}
            faceLabel={faceLabel}
            readOnly={notesReadOnly}
            busy={notesApi.create.isPending}
            error={noteError}
            onSelect={(id) => setSelection(id ? { kind: "note", id } : null)}
            onCreate={createNote}
            onCancelDraft={() => setNoteDraft(null)}
            onChange={(id, change) => changeNote(id, change)}
            onDone={(id) => changeNote(id, { status: "resolved" })}
            onAdd={() => setTool("note")}
          />
          <ShadeObjectSection
            item={selShade}
            obstacle={selObstacle}
            readOnly={readOnly}
            onShade={(t) => onShade(draft.shade.map((x) => (x.key === t.key ? t : x)))}
            onObstacle={(o) => onObstacles(draft.obstacles.map((x) => (x.key === o.key ? o : x)))}
            onDelete={deleteSelection}
            onDeselect={() => setSelection(null)}
          />
          {!automatic && <ShadingSection shading={shading} faces={faceAccess} heat={heatOn} onHeat={setHeatOn} sun={sunTime} onSun={setSunTime} drawn={drawnCount} pricing={live.pricing} />}
          <RoofSection
            sketch={draft.sketch}
            slopes={slopes}
            selection={selection}
            defaults={defaults}
            readOnly={readOnly}
            onDefaults={(d) => setDefaults((x) => ({ ...x, ...d }))}
            onFace={(id, change) => onSketch(updateFace(draft.sketch, id, "pitch" in change ? { ...change, pitchSource: "set" } : change))}
            onEdgeType={(id, type) => onSketch(setEdgeType(draft.sketch, id, type))}
            onDeleteEdge={(id) => {
              onSketch(removeEdge(draft.sketch, id, defaults));
              setSelection(null);
            }}
          />
          {tool !== "panel" && panelsSection}
          {!automatic && org.can("designs_leads", "create") && (
            <AiDesignCard
              balance={aiBalance}
              cost={designCost}
              busy={ai.isPending}
              error={ai.error && !(ai.error instanceof ApiError && ai.error.status === 402) ? errorMessage(ai.error) : null}
              onStart={(anchor) => void startAi(anchor)}
            />
          )}
        </aside>
  ));
  return (
    <div className={`sk-shell is-${layout}`}>
      <header className="sk-topbar">
        <Link className="sk-back" to={`/designs/${designId}`} aria-label={tx.t("sketch.editor.backToDesign")} title={tx.t("sketch.editor.backToDesign")}><DirIcon><ArrowLeft size={18} /></DirIcon></Link>
        <div className="sk-title">
          <b className="sk-address" dir="auto" title={doc.address || doc.design_name || ""}>{doc.address || doc.design_name}</b>
          <span className="sk-meta">
            <span className="sk-badge"><PenTool size={11} /> {automatic ? tx.t("sketch.editor.badgeAutomatic") : "Sketch"}</span>
            <span className="sk-version">{tx.t("sketch.editor.version", { version: shownVersion(doc.version_number, doc.version_offset) })}</span>
            {readOnly ? (
              <span className="sk-save-state"><Lock size={11} /> {tx.t("sketch.editor.readOnly")}</span>
            ) : (
              <span className={`sk-save-state${save.isPending ? " is-saving" : dirty ? " is-dirty" : ""}`}>{tx.t(save.isPending ? "sketch.common.saving" : dirty ? "sketch.editor.unsaved" : "sketch.editor.allSaved")}</span>
            )}
          </span>
        </div>
        <div className="sk-view-switch" role="radiogroup" aria-label={tx.t("sketch.editor.view")} style={{ "--i": view === "2d" ? 0 : 1, "--n": 2 } as CSSProperties}>
          <button type="button" role="radio" aria-checked={view === "2d"} className={view === "2d" ? "is-active" : ""} onClick={() => setView("2d")}><MapIcon size={15} /> {tx.t("sketch.editor.view2d")} <kbd className="sk-kbd">2</kbd></button>
          <button type="button" role="radio" aria-checked={view === "3d"} className={view === "3d" ? "is-active" : ""} onClick={() => setView("3d")} disabled={!roof.faces.length} title={roof.faces.length ? undefined : tx.t("sketch.editor.drawRoofFirstShort")}><Box size={15} /> {tx.t("sketch.editor.view3d")} <kbd className="sk-kbd">3</kbd></button>
        </div>
        <WorkspaceSwitch current="sketch" variant="sketch" onSwitch={workspace.go} shortcuts={workspaceKeys} design={workspaceDesign} />
        <div className="sk-topbar-actions">
          <HelpLink article="sketch/" label={tx.t("sketch.editor.help")} className="sk-icon-action" size={17} />
          <span className="sk-history">
            <button type="button" className="sk-icon-action" onClick={() => dispatch({ type: "undo" })} disabled={!history.past.length} aria-label={tx.t("sketch.editor.undo")} title={tx.t("sketch.editor.undoTitle", { keys: mac ? "⌘Z" : "Ctrl Z" })}><DirIcon><Undo2 size={17} /></DirIcon></button>
            <button type="button" className="sk-icon-action" onClick={() => dispatch({ type: "redo" })} disabled={!history.future.length} aria-label={tx.t("sketch.editor.redo")} title={tx.t("sketch.editor.redoTitle", { keys: mac ? "⇧⌘Z" : "Shift Ctrl Z" })}><DirIcon><Redo2 size={17} /></DirIcon></button>
          </span>
          <Button className="sk-save-btn" variant="primary" icon={<Save size={16} />} aria-label={tx.t("sketch.editor.save")} title={tx.t("sketch.editor.saveTitle", { keys: mac ? "⌘S" : "Ctrl S" })} onClick={doSave} loading={save.isPending} disabled={!dirty || readOnly}>
            <span className="sk-save-label">{tx.t("sketch.editor.save")}</span> <kbd className="sk-kbd">{mac ? KEYS_SAVE_MAC : KEYS_SAVE}</kbd>
          </Button>
        </div>
      </header>
      <div className="sk-body">
        <main className="sk-stage">
          <StageBar tool={tool} placeMode={placeMode} onPlaceMode={setPlaceMode} orientation={orientation} onOrientation={setOrientation} corners={corners} structureName={active.name} hint={stageHint} readOnly={readOnly} />
          <div className="sk-stage-map">
          <SketchToolbar tool={tool} onTool={setTool} readOnly={readOnly} state={toolState} onVariant={onVariant} collapsible={layout === "phone"} />
          {picks && view === "3d" && (
            <div className="sk-pick-cue sk-glass" role="status">
              <span className="sk-pick-step">{picks.length + 1}/2</span>
              {tx.t(picks.length ? "sketch.editor.pickEave" : "sketch.editor.pickGround", { name: structureLabel(tx, active.name) })}
              <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.common.cancel")} onClick={() => setPicks(null)}><X size={14} /></button>
            </div>
          )}
          {!automatic && view === "2d" && !aligning && (
            <div className="sk-basemap-dock">
              <BasemapMenu
                data={basemapData.data}
                current={doc.basemap?.provider ?? "google_static"}
                readOnly={readOnly}
                busy={basemapActions.upload.isPending || basemapActions.choose.isPending}
                error={basemapError}
                onChoose={(key, uploadId) => {
                  setBasemapError(null);
                  basemapActions.choose.mutate({ provider: key as S["BasemapProvider"] | null, upload_id: uploadId ?? null }, { onError: (e) => setBasemapError(errorMessage(e)) });
                }}
                onUpload={(file) => {
                  setBasemapError(null);
                  basemapActions.upload.mutate(file, { onSuccess: (u) => (u.px_to_enu ? basemapActions.choose.mutate({ provider: "upload", upload_id: u.id }) : startAlign(u)), onError: (e) => setBasemapError(errorMessage(e)) });
                }}
                onAlign={startAlign}
                onDelete={(id) => basemapActions.remove.mutate(id)}
              />
            </div>
          )}
          {aligning && (
            <AlignPanel
              upload={aligning.upload}
              mode={aligning.mode}
              onMode={(mode) => setAligning((a) => (a ? { ...a, mode, points: [] } : a))}
              opacity={aligning.opacity}
              onOpacity={(opacity) => setAligning((a) => (a ? { ...a, opacity } : a))}
              points={aligning.points}
              miss={alignFit?.miss ?? null}
              scaleCm={metresPerPx(alignFit?.m ?? aligning.m) * 100}
              canSave={aligning.mode === "drag" ? plausible(aligning.m) : !!alignFit && plausible(alignFit.m)}
              busy={basemapActions.align.isPending}
              error={basemapError}
              onClearPoints={() => setAligning((a) => (a ? { ...a, points: [] } : a))}
              onCancel={() => setAligning(null)}
              onSave={saveAlign}
            />
          )}
          {view === "2d" ? (
            <SketchCanvas
              {...shared}
              sketch={draft.sketch}
              slopes={slopes}
              lineType={lineType}
              defaults={drawDefaults}
              onSketch={onSketch}
              hiddenFaces={hidden}
              activeStructure={structures.length > 1 ? active.id : undefined}
              dormerKind={dormerKind}
              footprintSide={selectedSide ? { mode: selectedSide.mode, allowed: selectedSide.allowed } : null}
              onToggleSide={flipSide}
              align={alignState}
              onFootprint={setFootprint}
              onCorners={setCorners}
              hintBoxes={hintBoxes}
              flashFaces={flash}
              settleEdits={!automatic}
              gaps={gapMarks}
            />
          ) : (
            <Suspense fallback={<Spinner label={tx.t("sketch.editor.loading3d")} />}>
              <Sketch3D {...shared} dormers={(draft.sketch.dormers ?? []).map((d) => d.id)} surroundings={shownSurroundings} onSurroundingsError={onSurroundingsError} onPick={picks ? onPick : null} gaps={gaps3d} onGap={setGapPicked} />
            </Suspense>
          )}
          {view === "2d" && !draft.sketch.faces.length && !readOnly && corners === 0 && !footprint && <EmptyState tool={tool} onTrace={() => setTool("footprint")} />}
          {view === "2d" && picture.failed && !pictureDismissed && (
            <div className="sk-map-notice sk-glass" role="status">
              <ImageOff size={16} />
              <span>{tx.t("sketch.editor.pictureFailed")}</span>
              <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.common.dismiss")} onClick={() => setPictureDismissed(true)}><X size={14} /></button>
            </div>
          )}
          {gapNotice && gaps.length > 0 && !gapsOpen && !readOnly && (
            <div className="sk-gapbar" role="status" aria-live="polite">
              <span className="sk-gapbar-mark"><TriangleAlert size={16} /></span>
              <span className="sk-gapbar-text"><Msg id="sketch.editor.gapsFound" values={{ count: gaps.length, b: (c) => <b>{c}</b> }} /></span>
              <button type="button" className="sk-action is-primary" onClick={fixAllGaps}>{tx.t("sketch.editor.closeThem", { count: gaps.length })}</button>
              <button type="button" className="sk-action is-ghost" onClick={openGaps}>{tx.t("sketch.structures.showShort")}</button>
              <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.common.dismiss")} onClick={() => setGapNotice(false)}><X size={14} /></button>
            </div>
          )}
          {purchasePrompt && !readOnly && (
            <div className={`sk-purchase sk-glass${purchasePrompt.waiting ? " is-waiting" : ""}`} role="status" aria-live="polite">
              <span className="sk-purchase-mark">{purchasePrompt.waiting ? <Loader2 size={20} className="sk-spin" /> : <LuxGlyph size={22} />}</span>
              <div className="sk-purchase-text">
                <b>{purchasePrompt.title}</b>
                <span>{purchasePrompt.detail}</span>
              </div>
              {!purchasePrompt.waiting && purchasePrompt.canRun && (
                <button type="button" className="sk-action is-primary" onClick={(e) => runPurchased(e.currentTarget)}>
                  {purchasePrompt.action}
                </button>
              )}
              <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.editor.notNowAction", { action: tx.t(ACTION_LABEL[purchase!.action]) })} onClick={() => setPurchase(null)}><X size={14} /></button>
            </div>
          )}
          {!automatic && (
            <div className={`sk-assist-dock${compact && !assistOpen && !hints && !assistError ? " is-folded" : ""}`}>
              {compact && (
                <button type="button" className={`sk-assist-fold sk-glass${assistOpen ? " is-open" : ""}`} aria-expanded={assistOpen} aria-label={tx.t(assistOpen ? "sketch.editor.closeAssist" : "sketch.assist.label")} onClick={() => setAssistOpen((o) => !o)}>
                  {assistOpen ? <X size={16} /> : <Sparkles size={16} />}
                  <span>{tx.t("sketch.assist.label")}</span>
                  {!assistOpen && gaps.length > 0 && <i className="sk-roofcheck-count">{gaps.length}</i>}
                </button>
              )}
              <AssistBar
                features={features}
                balance={assistBalance}
                busy={roofHints.isPending ? "roof_hints" : surroundingsApi.start.isPending ? "surroundings" : null}
                surroundingsOn={surroundingsOn}
                surroundingsSession={sessionActive(assistSession)}
                readOnly={readOnly}
                onRun={(key, anchor) => void runAssist(key, anchor)}
                roofCheck={draft.sketch.faces.length ? { gaps: gaps.length, on: gapsOpen, onRun: () => (gapsOpen ? setGapsOpen(false) : openGaps()) } : null}
              />
              {assistError && (
                <div className="sk-assist-error sk-glass" role="alert">
                  <span>{assistError}</span>
                  <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.common.dismiss")} onClick={() => setAssistError(null)}><X size={14} /></button>
                </div>
              )}
              {nudgeFace && (
                // under the Assist bar, not on the face: on the face it caught the clicks meant for the face
                <Nudge label={hintNudgeLabel(tx, roofHintsFeature)} onClick={() => void runAssist("roof_hints", null)} onDismiss={() => dismissNudge("roof_hints")} />
              )}
              {showSurNudge && (
                <Nudge label={tx.t("sketch.editor.nudgeSurroundings")} onClick={() => { setSurNudgeShown(true); void runAssist("surroundings", null); }} onDismiss={() => { setSurNudgeShown(true); dismissNudge("surroundings"); }} />
              )}
              {hints && view === "2d" && (
                <HintsPanel
                  hints={hints}
                  sketch={draft.sketch}
                  decided={decided}
                  onAccept={(id) => acceptHints([id])}
                  onReject={(id) => setDecided((d) => ({ ...d, [id]: "rejected" }))}
                  onAcceptAll={() => acceptHints(openHints)}
                  onClose={() => setHints(null)}
                />
              )}
            </div>
          )}
          </div>
        </main>
        {sidePanel}
      </div>
      {sheetOpen && <CheatSheet mac={mac} overrides={overrides} onOverrides={setOverrides} onClose={() => setSheetOpen(false)} groups={["Sketch", "Help"]} />}
      {footprint && <ShapeChooser corners={footprint} onChoose={chooseShape} onClose={() => setFootprint(null)} />}
      {aiStarted && (
        <BuildProgress
          designId={aiStarted.design_id}
          jobId={aiStarted.job_id ?? null}
          address={doc.address || doc.design_name || ""}
          jobOptions={aiStarted.job_options ?? {}}
          onClose={() => setAiStarted(null)}
          onReady={() => navigate(`/designs/${aiStarted.design_id}/edit`)}
        />
      )}
      {workspace.dialog}
    </div>
  );
}

/** The host face of a dormer face (`d1-f2` → the dormer's face), else null. */
function dormerHost(s: { dormers?: { id: string; faceId: string }[] }, faceId: string): string | null {
  const d = (s.dormers ?? []).find((x) => faceId.startsWith(`${x.id}-f`));
  return d ? d.faceId : null;
}

/** The preview's setbacks when they are for the faces as drawn now (same faces and planes), else none. */
function setbacksFor(setbacks: S["SetbacksOutput"] | null, model: S["RoofModelOutput"]): S["SetbacksOutput"] | null {
  if (!setbacks) return null;
  const ids = new Set(model.faces.map((f) => f.id));
  return setbacks.faces.length === ids.size && setbacks.faces.every((f) => ids.has(f.face_id)) ? setbacks : null;
}

/** The first-run card on an empty map: what to do, in three short steps. */
function EmptyState({ tool, onTrace }: { tool: SketchTool; onTrace: () => void }) {
  const tx = useSketchText();
  return (
    <div className="sk-empty sk-glass" role="note">
      <div className="sk-empty-art" aria-hidden>
        <svg viewBox="0 0 112 88">
          <rect x="26" y="20" width="60" height="48" rx="2" fill="rgba(59,91,255,0.12)" transform="rotate(-8 56 44)" />
          <path d="M28 26 L82 18 L88 62 L34 70 Z" fill="none" stroke="#3b5bff" strokeWidth="2.5" strokeLinejoin="round" className="sk-empty-trace" />
          <path d="M31 48 L85 40" stroke="#ffb020" strokeWidth="2.5" strokeLinecap="round" className="sk-empty-dot" />
          {[[28, 26], [82, 18], [88, 62], [34, 70]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3.6" fill="#fff" stroke="#3b5bff" strokeWidth="2" className="sk-empty-dot" style={{ animationDelay: `${i * 0.35}s` }} />
          ))}
        </svg>
      </div>
      <div>
        <h2>{tx.t("sketch.empty.title")}</h2>
        <p>{tx.t("sketch.empty.text")}</p>
        <div className="sk-empty-keys">
          {tool === "footprint" ? (
            <span><Msg id="sketch.empty.enter" values={{ kbd }} /></span>
          ) : (
            <button type="button" className="sk-action is-primary" onClick={onTrace}><SquareDashed size={14} /> {tx.t("sketch.tool.footprint")}</button>
          )}
          <span><Msg id="sketch.empty.alt" values={{ kbd }} /></span>
          <span><Msg id="sketch.empty.lines" values={{ kbd }} /></span>
          <span><Msg id="sketch.empty.esc" values={{ kbd }} /></span>
        </div>
      </div>
    </div>
  );
}

const ANY_HINT: Partial<Record<RoofShape, MessageKey>> = {
  hip: "sketch.shapes.hipAny",
  gable: "sketch.shapes.gableAny",
};

const SHAPES: [RoofShape, MessageKey, MessageKey][] = [
  ["gable", "sketch.dormer.gable", "sketch.shapes.gableHint"],
  ["gable-across", "sketch.shapes.gableAcross", "sketch.shapes.gableAcrossHint"],
  ["hip", "sketch.dormer.hip", "sketch.shapes.hipHint"],
  ["shed", "sketch.dormer.shed", "sketch.shapes.shedHint"],
  ["flat", "sketch.pitch.flat", "sketch.shapes.flatHint"],
];

/** After a footprint: the roof shape (gable and hip need four right-angled corners). Keys 1–5 pick one. */
function ShapeChooser({ corners, onChoose, onClose }: { corners: Vec2[]; onChoose: (s: RoofShape) => void; onClose: () => void }) {
  const tx = useSketchText();
  const rect = corners.length === 4 && isRectangle(corners);
  // hip and gable work on any outline (the straight skeleton); "ridge across" needs a rectangle's long sides
  const allowed = (shape: RoofShape) => shape !== "gable-across" || rect;
  return (
    <Modal
      title={tx.t("sketch.stage.step.pickShape")}
      onClose={onClose}
      width={600}
      onKeyDown={(e) => {
        const shape = SHAPES[Number(e.key) - 1]?.[0];
        if (!shape) return;
        // the digit is the chooser's: it must not also switch 2D/3D (the editor's 2 and 3)
        e.preventDefault();
        e.stopPropagation();
        if (allowed(shape)) onChoose(shape);
      }}
    >
      <p className="sk-shapes-lead">{tx.t("sketch.shapes.lead")}</p>
      <div className="sk-shapes">
        {SHAPES.map(([shape, label, hint], i) => (
          <button key={shape} type="button" className={`sk-shape sk-shape-${shape}`} disabled={!allowed(shape)} onClick={() => onChoose(shape)}>
            <span className="sk-shape-art"><RoofArt shape={shape} /></span>
            <kbd className="sk-kbd">{i + 1}</kbd>
            <b>{!allowed(shape) && <Lock size={12} />}{tx.t(label)}</b>
            <span>{tx.t(allowed(shape) ? (rect ? hint : ANY_HINT[shape] ?? hint) : "sketch.shapes.needsSquare")}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

/** A small 3/4 view of each roof shape (walls, faces in three tones, the ridge in its line colour). */
function RoofArt({ shape }: { shape: RoofShape }) {
  // footprint corners (front-left, front-right, back-right, back-left) and the wall height
  const A = [14, 58], B = [78, 58], C = [106, 42], D = [42, 42];
  const H = 20;
  const down = (q: number[]) => [q[0]!, q[1]! + H];
  const mid = (p: number[], q: number[], lift: number) => [(p[0]! + q[0]!) / 2, (p[1]! + q[1]!) / 2 - lift];
  const P = (...pts: number[][]) => pts.map((q) => q.join(",")).join(" ");
  const walls = (
    <>
      <ellipse cx="60" cy="80" rx="52" ry="6" fill="currentColor" opacity="0.08" />
      <polygon points={P(A, B, down(B), down(A))} className="sk-roof-wall" />
      <polygon points={P(B, C, down(C), down(B))} className="sk-roof-wall is-side" />
    </>
  );
  let roof;
  if (shape === "gable") {
    const R1 = mid(A, D, 24), R2 = mid(B, C, 24);
    roof = (
      <>
        <polygon points={P(R1, R2, C, D)} className="sk-roof-c" />
        <polygon points={P(B, C, R2)} className="sk-roof-wall is-side" />
        <polygon points={P(A, B, R2, R1)} className="sk-roof-a" />
        <line x1={R1[0]} y1={R1[1]} x2={R2[0]} y2={R2[1]} className="sk-roof-line" />
      </>
    );
  } else if (shape === "gable-across") {
    const R1 = mid(A, B, 26), R2 = mid(D, C, 26);
    roof = (
      <>
        <polygon points={P(A, R1, R2, D)} className="sk-roof-c" />
        <polygon points={P(A, B, R1)} className="sk-roof-wall" />
        <polygon points={P(R1, B, C, R2)} className="sk-roof-b" />
        <line x1={R1[0]} y1={R1[1]} x2={R2[0]} y2={R2[1]} className="sk-roof-line" />
      </>
    );
  } else if (shape === "hip") {
    const m1 = mid(A, D, 24), m2 = mid(B, C, 24);
    const R1 = [m1[0]! + 14, m1[1]!], R2 = [m2[0]! - 14, m2[1]!];
    roof = (
      <>
        <polygon points={P(D, C, R2, R1)} className="sk-roof-c" />
        <polygon points={P(A, R1, D)} className="sk-roof-c" />
        <polygon points={P(B, C, R2)} className="sk-roof-b" />
        <polygon points={P(A, B, R2, R1)} className="sk-roof-a" />
        <polyline points={P(A, R1)} className="sk-roof-hip" />
        <polyline points={P(B, R2, C)} className="sk-roof-hip" />
        <line x1={R1[0]} y1={R1[1]} x2={R2[0]} y2={R2[1]} className="sk-roof-line" />
      </>
    );
  } else if (shape === "shed") {
    const Dh = [D[0]!, D[1]! - 18], Ch = [C[0]!, C[1]! - 18];
    roof = (
      <>
        <polygon points={P(B, C, Ch)} className="sk-roof-wall is-side" />
        <polygon points={P(A, B, Ch, Dh)} className="sk-roof-a" />
        <line x1={A[0]} y1={A[1]} x2={B[0]} y2={B[1]} className="sk-roof-eave" />
      </>
    );
  } else {
    roof = (
      <>
        <polygon points={P(A, B, C, D)} className="sk-roof-c" />
        <polygon points={P([A[0]! + 6, A[1]! - 2], [B[0]! - 2, B[1]! - 2], [C[0]! - 6, C[1]! + 2], [D[0]! + 2, D[1]! + 2])} className="sk-roof-a" opacity={0.5} />
      </>
    );
  }
  return (
    <svg viewBox="0 0 120 90">
      {walls}
      {roof}
    </svg>
  );
}

/** A per-viewer flag in localStorage (a dismissed nudge); never required: private windows simply show it again. */
function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string) {
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* private mode */
  }
}

/** The stage bar's line for the active tool: what a click does, and the keys that help (the long part, after the
 * first sentence, hides on narrow screens: `<long>`). */
function toolHint(tool: SketchTool, neighbour: "building" | "wall", sizeKeys: string, kindKeys: string): ReactNode {
  const values = { kbd, long: (c: ReactNode[]) => <span className="sk-hint-long">{c}</span>, size: sizeKeys, kind: kindKeys };
  const key: MessageKey =
    tool === "neighbour" ? (neighbour === "building" ? "sketch.hint.neighbourBuilding" : "sketch.hint.neighbourWall")
    : HINT[tool];
  return <Msg id={key} values={values} />;
}

const HINT: Record<Exclude<SketchTool, "neighbour">, MessageKey> = {
  select: "sketch.hint.select",
  pan: "sketch.hint.pan",
  footprint: "sketch.hint.footprint",
  line: "sketch.hint.line",
  dormer: "sketch.hint.dormer",
  panel: "sketch.hint.panel",
  tree: "sketch.hint.tree",
  obstacle: "sketch.hint.obstacle",
  note: "sketch.hint.note",
};
